import React from "react";
import { motion } from "framer-motion";
import process1 from "@/assets/process-1.jpg";
import process2 from "@/assets/process-2.jpg";
import process3 from "@/assets/process-3.jpg";
import process4 from "@/assets/process-4.jpg";
interface ProcessStepProps {
  number: string;
  image: string;
  title: string;
  description: string;
  index: number;
}

function ProcessStep({ number, image, title, description, index }: ProcessStepProps) {
  return (
    <motion.div
      className="flex gap-6"
      initial={{ opacity: 0, x: 30 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.5, ease: "easeOut", delay: index * 0.1 }}
    >
      <span className="text-h2 text-anchor shrink-0">{number}</span>
      <div className="flex flex-col gap-4">
        <img src={image} alt={title} className="w-full max-w-[400px] h-auto rounded-[16px] object-cover" width={976} height={640} loading="lazy" decoding="async" />
        <h3 className="text-h4 text-foreground">{title}</h3>
        <p className="text-body text-anchor max-w-[400px]">{description}</p>
      </div>
    </motion.div>
  );
}

const steps = [
  {
    number: "1",
    image: process1,
    title: "Discovery & Alignment",
    description: "Define business goals, map workflows, and identify populations.",
  },
  {
    number: "2",
    image: process2,
    title: "Guided Implementation",
    description: "Custom roadmap with milestones, training, and full configuration support.",
  },
  {
    number: "3",
    image: process3,
    title: "Launch Strategy",
    description: "Execute all-hands onboarding, tailored education, and proactive engagement strategies.",
  },
  {
    number: "4",
    image: process4,
    title: "Engage, Monitor & Measure",
    description:
      "Dedicated account services, continuous consultation, data and analytics, and priority support aligned to your plan objectives.",
  },
];

export function HowItWorksSection() {
  return (
    <section
      className="w-full"
      style={{ background: "linear-gradient(to bottom, hsla(160, 30%, 67%, 0.4), hsla(160, 30%, 67%, 0))" }}
      aria-labelledby="how-it-works-heading"
    >
      <div className="w-full max-w-[1440px] mx-auto px-4 sm:px-8 md:px-12 lg:px-20 3xl:px-28 4xl:px-32">
        <div className="flex gap-16 max-lg:flex-col max-lg:gap-0">
          {/* Left column - Sticky header */}
          <motion.div
            className="w-1/2 shrink-0 py-24 max-lg:w-full max-lg:pt-12 max-lg:pb-12"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <div className="lg:sticky lg:top-32">
              <span className="text-caption text-beacon uppercase pl-[3px] mb-4 block">How It Works</span>
              <h2 id="how-it-works-heading" className="text-h1 text-foreground mb-6">
                End-to-End Enablement. Minimal Operational Lift.
              </h2>
              <p className="text-body text-anchor">
                We partner with your team to design, launch, and scale a program tailored to your population, workflows,
                and success metrics.
              </p>
            </div>
          </motion.div>

          {/* Right column - Scrolling steps */}
          <div className="flex-1 flex flex-col gap-20 py-24 max-lg:pt-0 max-lg:pb-16 max-lg:gap-16">
            {steps.map((step, index) => (
              <ProcessStep
                key={index}
                number={step.number}
                image={step.image}
                title={step.title}
                description={step.description}
                index={index}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
